import { 
    Button,
    PasswordInput,
    Container,
    Center,
    Text,
    Title,
    Card,
    Popover
} from '@mantine/core';

import { 
    IconLockOpen,
    IconCircleCheck
} from '@tabler/icons-react';

import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver';

import { z } from 'zod';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useResetPasswordUser } from '@/features/auth/api/resetPassword';
import PasswordChecklist from "react-password-checklist"
import { useViewportSize } from '@mantine/hooks';

const resetInitValues = {
    password: '',
    passwordConfirm: ''
}

export const ResetPassword = () => {
    const { t } = useTranslation();
    const { token, user_uuid } = useParams();
    const useResetPasswordMutation = useResetPasswordUser();
    const { height } = useViewportSize();

    const schema = z.object({
        password: z.string()
            .nonempty(t('error.cannotBeEmpty')),
        passwordConfirm: z.string()
            .nonempty(t('error.cannotBeEmpty')),
        token: z.string()
            .nonempty(t('error.cannotBeEmpty')),    
        user_uuid: z.string()
            .nonempty(t('error.cannotBeEmpty')),
    }).refine((data) => data.password === data.passwordConfirm, {
        message: t('password.passwordDontMatch'),
        path: ["passwordConfirm"],
    });

    const [isSuccess, setIsSuccess] = useState(false);
    const [passwordValid, setPasswordValid] = useState(false);

    const form = useForm({
        initialValues: {
            ...resetInitValues,
            token: token,
            user_uuid: user_uuid
        },
        validate: zodResolver(schema)
    })

    async function handleSubmit(e: React.SyntheticEvent) {
        e.preventDefault();

        const values = form.values;
        const resetPasswordObject = {
            password: values.password!,
            passwordConfirmation: values.passwordConfirm!,
        }

        await useResetPasswordMutation.mutateAsync({ data: resetPasswordObject, token: values.token!, user_uuid: values.user_uuid! })
    }

    useEffect(() => {
        if (useResetPasswordMutation.isSuccess)
            setIsSuccess(true)
    }, [useResetPasswordMutation.isSuccess])


    if (isSuccess) {
        return (
            <Center maw={400} mx="auto" h={height}>

                <Container maw={400}>
                    <Card shadow="sm" padding="lg" radius="md" withBorder mb="md" mt="md">
                        <Center>
                            <IconCircleCheck color="blue" size={60} />
                        </Center>
                        <Title>{t('password.passwordChanged')}</Title>
                        <Text>{t('password.passwordChangedMessage')}</Text>
                        <Center>
                            <Link to={'/login'}>
                                <Text>{t('action.backToLogin')}</Text>
                            </Link>
                        </Center>
                    </Card>
                </Container>
            </Center>
        )
    }

    return (
        <Center maw={400} mx="auto" h={height}>
            <Container maw={400}>
                <Card shadow="sm" padding="lg" radius="md" withBorder mb="md" mt="md">
                    <Center>
                        <IconLockOpen color="blue" size={60} />
                    </Center>
                    <Title>{t('password.resetPassword')}</Title>
                    <Text fz="sm">{t('password.resetPasswordMessage')}</Text>
                    <form id="reset-password-form" onSubmit={handleSubmit}>
                        <Popover width="target" position="bottom" transitionProps={{ transition: 'pop' }}>
                            <Popover.Target>
                                <PasswordInput
                                    withAsterisk
                                    name="password"
                                    label={t('user.password')}
                                    {...form.getInputProps('password')} />
                            </Popover.Target>
                            <Popover.Dropdown>
                                <PasswordChecklist
                                        rules={["minLength","specialChar","number","capital"]}
                                        minLength={8}
                                        value={form.values.password}
                                        valueAgain={form.values.passwordConfirm}
                                        onChange={(isValid) => setPasswordValid(isValid)}
                                        messages={{
                                            minLength: t('validation.minLength'),
                                            specialChar: t('validation.specialChar'),
                                            number: t('validation.number'),
                                            capital: t('validation.capital')
                                        }}
                                    />
                            </Popover.Dropdown>
                        </Popover>
                        <PasswordInput
                            name="passwordConfirm"
                            label={t('user.passwordConfirm')}
                            {...form.getInputProps('passwordConfirm')} />
                        <Center mt={8} mb={8}>
                            <Button 
                                disabled={!passwordValid}
                                type='submit'
                                form='reset-password-form'
                                style={{ backgroundColor: '#ff0008', width: '100%'}}>
                                {t('action.reset')}
                            </Button>
                        </Center>
                    </form>
                </Card>
            </Container>
        </Center>
    )
    
}